import React, {useState, useEffect} from 'react';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import InputText from '../FormFields/InputText';
import SelectInfinite from '../FormFields/SelectInfinite';
import TextArea from '../FormFields/Textarea';
import InputDate from '../FormFields/inputDate/InputDate';
import {getLanguage, t} from 'react-switch-lang';
import * as yup from 'yup';
import {useForm, FormProvider} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers';
import {addMeasure} from '../../services/measuresService/measuresApi';
import {getAllUsers} from '../../services/usersService';
import {format} from 'date-fns';
import Toast from '../toast/Toast';
import {validDouble} from '../../utils/const';
import allowedFileTypes from '../../utils/allowedFileTypes';
import TransitionModal from '../preventTransitionprompt/TransitionModal';
import PreventTransitionPrompt from '../preventTransitionprompt/PreventTransition';
import Trash from '../../app/Measures/img/Trash.svg';
import SelectList from '../../components/FormFields/select/SelectList';
import {Spin} from 'antd';

function MeasuresFromAdd({
  userData,
  toggleOpen,
  departmentData,
  open,
  id,
  tab,
  estimatedGrossLost,
}) {
  const [users, setUsers] = useState();
  const [loading, setLoading] = useState(true);
  const [disableBtn, setDisableBtn] = useState(false);
  const [files, setFiles] = useState([]);
  const [isAdding, setIsAdding] = useState(true);
  const [transitionModal, setTransitionModal] = useState(false);

  const fetchUsers = async () => {
    try {
      const {data} = await getAllUsers();
      setUsers(data);
      return data;
    } catch (e) {}
  };

  useEffect(() => {
    fetchUsers().then((r) => {
      let usersMap = [];
      r.forEach((item) => {
        usersMap.push({
          value: item.id,
          label: item.firstName + ' ' + item.lastName,
        });
      });
      setUsers(usersMap);
      setLoading(false);
    });
  }, []);

  const [schema, setScheme] = useState(
    yup.object().shape({
      realizationDate: yup
        .string()
        .nullable()
        .trim()
        .required(t('message.fieldRequired')),
      riskAnalysis: yup
        .string()
        .trim()
        .required(t('message.fieldRequired'))
        .max(2000, t('message.max', {max: 2000})),
      levelOfMeasureRealization: yup
        .object()
        .nullable()
        .required(t('message.fieldRequired')),
      cost: yup.object().nullable().required(t('message.fieldRequired')),
      department: yup.object().nullable().required(t('message.fieldRequired')),
      grossLoss: yup
        .string()
        .trim()
        .matches(validDouble, t('message.onlyDecimalNumbers'))
        .typeError(t('message.onlyDecimalNumbers')),
      lossChargedInsurance: yup
        .string()
        .trim()
        .matches(validDouble, t('message.onlyDecimalNumbers'))
        .typeError(t('message.onlyDecimalNumbers')),
      lossChargedOther: yup
        .string()
        .trim()
        .matches(validDouble, t('message.onlyDecimalNumbers'))
        .typeError(t('message.onlyDecimalNumbers')),
      netLoss: yup
        .string()
        .trim()
        .matches(validDouble, t('message.onlyDecimalNumbers'))
        .typeError(t('message.onlyDecimalNumbers')),
        hdRequestId: yup
            .string()
            .trim(),
      mitigationPercentage: yup
        .string()
        .trim()
        .matches(validDouble, t('message.onlyDecimalNumbers'))
        .typeError(t('message.onlyDecimalNumbers')),
      mitigationMeasures: yup
        .string()
        .trim()
        .required(t('message.fieldRequired'))
        .max(2000, t('message.max', {max: 2000})),
      notStartedReason: yup
        .string()
        .trim()
        .max(1000, t('message.max', {max: 1000})),
      resultOfApplied: yup
        .string()
        .trim()
        .max(1000, t('message.max', {max: 1000})),
    })
  );

  const l = getLanguage();

  const optionsTask = [
    {value: 'NOTSTARTED', label: `${t('mitigation-measures.notStarted')}`},
    {value: 'INPROGRESS', label: `${t('mitigation-measures.inProgress')}`},
    {value: 'REALIZED', label: `${t('mitigation-measures.realized')}`},
  ];

  const methods = useForm({
    resolver: yupResolver(schema),
  });

  const {handleSubmit, watch, setValue} = methods;
  const wathcLevelOfRealization = watch('levelOfMeasureRealization');

  const submitForm = async (submitedData) => {
    setDisableBtn(true);
    const files = document.getElementById('upload').files;
    const date =
      format(new Date(submitedData.realizationDate), 'yyyy-MM-dd') +
      'T' +
      format(new Date(submitedData.realizationDate), 'hh:mm:ss');

    const payload = {
      mitigationMeasures: submitedData.mitigationMeasures,
      costEstimationId: submitedData.cost.value,
      departmentInChargeId: submitedData.department.value,
      grossLoss: parseFloat(watch('grossLoss')),
      lossChargedInsurance: parseFloat(submitedData.lossChargedInsurance),
      lossChargedOther: parseFloat(submitedData.lossChargedOther),
      mitigationPercentage: parseFloat(submitedData.mitigationPercentage),
       hdRequestId: submitedData.hdRequestId,
      netLoss: parseFloat(watch('netLoss')),
      riskAnalysis: submitedData.riskAnalysis,
      realizationDate: date,
      notStartedReason: submitedData.notStartedReason,
      levelOfMeasureRealization: submitedData.levelOfMeasureRealization.value,
      userResponsibleId: submitedData.userResponsibleId.value,
      riskId: id,
      userCreatedId: userData.id,
      additionalMitigationMeasures: submitedData.additionalMitigationMeasures,
      appliedMeasureResult: {name: submitedData.resultOfApplied},
    };
    try {
      const response = await addMeasure(payload, files);
      if (response.status === 201) {
        await Toast.fire({
          title: t('message.addSuccess3', {entity: t('common.measure')}),
          icon: 'success',
        });
        setIsAdding(false);
      }
      await window.location.reload();
      setDisableBtn(false);
    } catch (err) {
      if (err.response.data.errorKey === 'notInProgress') {
        await Toast.fire({
          title: t('message.riskNotInprogress'),
          icon: 'error',
        });
      }
      if (err.response.code === 'file-format') {
        await Toast.fire({
          title: t('message.fileFormatNotAllowed'),
          icon: 'error',
        });
      }
      if (err.response.status === 400) {
        await Toast.fire({
          title: t('message.errorApi'),
          icon: 'error',
        });
        setIsAdding(false);
      }
      toggleOpen(!open);
    }
  };

  useEffect(() => {
    if (wathcLevelOfRealization?.value) {
      if (wathcLevelOfRealization?.value === 'NOTSTARTED') {
        setScheme(
          yup.object().shape({
            realizationDate: yup
              .string()
              .nullable()
              .trim()
              .required(t('message.fieldRequired')),
            riskAnalysis: yup
              .string()
              .trim()
              .required(t('message.fieldRequired'))
              .max(2000, t('message.max', {max: 2000})),
            levelOfMeasureRealization: yup
              .object()
              .nullable()
              .required(t('message.fieldRequired')),
            cost: yup.object().nullable().required(t('message.fieldRequired')),
            department: yup
              .object()
              .nullable()
              .required(t('message.fieldRequired')),
            notStartedReason: yup
              .string()
              .trim()
              .required(t('message.fieldRequired'))
              .max(1000, t('message.max', {max: 1000})),
            grossLoss: yup
              .string()
              .trim()
              .matches(validDouble, t('message.onlyDecimalNumbers'))
              .typeError(t('message.onlyDecimalNumbers')),
            lossChargedInsurance: yup
              .string()
              .trim()
              .matches(validDouble, t('message.onlyDecimalNumbers'))
              .typeError(t('message.onlyDecimalNumbers')),
            lossChargedOther: yup
              .string()
              .trim()
              .matches(validDouble, t('message.onlyDecimalNumbers'))
              .typeError(t('message.onlyDecimalNumbers')),
            netLoss: yup
              .string()
              .trim()
              .matches(validDouble, t('message.onlyDecimalNumbers'))
              .typeError(t('message.onlyDecimalNumbers')),
            mitigationMeasures: yup
              .string()
              .trim()
              .required(t('message.fieldRequired'))
              .max(2000, t('message.max', {max: 2000})),
              hdRequestId:yup
                  .string()
                  .trim(),
            mitigationPercentage: yup
              .string()
              .trim()
              .matches(validDouble, t('message.onlyDecimalNumbers'))
              .typeError(t('message.onlyDecimalNumbers')),
            resultOfApplied: yup
              .string()
              .trim()
              .max(1000, t('message.max', {max: 1000})),
          })
        );
      } else if (wathcLevelOfRealization?.value === 'INPROGRESS') {
        setScheme(
          yup.object().shape({
            realizationDate: yup
              .string()
              .nullable()
              .trim()
              .required(t('message.fieldRequired')),
            riskAnalysis: yup
              .string()
              .trim()
              .required(t('message.fieldRequired'))
              .max(2000, t('message.max', {max: 2000})),
            levelOfMeasureRealization: yup
              .object()
              .nullable()
              .required(t('message.fieldRequired')),
            cost: yup.object().nullable().required(t('message.fieldRequired')),
            department: yup
              .object()
              .nullable()
              .required(t('message.fieldRequired')),
            resultOfApplied: yup
              .string()
              .trim()
              .required(t('message.fieldRequired'))
              .max(1000, t('message.max', {max: 1000})),
            grossLoss: yup
              .string()
              .trim()
              .matches(validDouble, t('message.onlyDecimalNumbers'))
              .typeError(t('message.onlyDecimalNumbers')),
            lossChargedInsurance: yup
              .string()
              .trim()
              .matches(validDouble, t('message.onlyDecimalNumbers'))
              .typeError(t('message.onlyDecimalNumbers')),
            lossChargedOther: yup
              .string()
              .trim()
              .matches(validDouble, t('message.onlyDecimalNumbers'))
              .typeError(t('message.onlyDecimalNumbers')),
            netLoss: yup
              .string()
              .trim()
              .matches(validDouble, t('message.onlyDecimalNumbers'))
              .typeError(t('message.onlyDecimalNumbers')),
            mitigationMeasures: yup
              .string()
              .trim()
              .required(t('message.fieldRequired'))
              .max(2000, t('message.max', {max: 2000})),
            notStartedReason: yup
              .string()
              .trim()
              .max(1000, t('message.max', {max: 1000})),
              hdRequestId:yup
                  .string()
                  .trim(),
            mitigationPercentage: yup
              .string()
              .trim()
              .matches(validDouble, t('message.onlyDecimalNumbers'))
              .typeError(t('message.onlyDecimalNumbers')),
          })
        );
      } else {
        setScheme(
          yup.object().shape({
            realizationDate: yup
              .string()
              .nullable()
              .trim()
              .required(t('message.fieldRequired')),
            riskAnalysis: yup
              .string()
              .trim()
              .required(t('message.fieldRequired'))
              .max(2000, t('message.max', {max: 2000})),
            levelOfMeasureRealization: yup
              .object()
              .nullable()
              .required(t('message.fieldRequired')),
            cost: yup.object().nullable().required(t('message.fieldRequired')),
            department: yup
              .object()
              .nullable()
              .required(t('message.fieldRequired')),
            resultOfApplied: yup
              .string()
              .trim()
              .required(t('message.fieldRequired'))
              .max(1000, t('message.max', {max: 1000})),
            grossLoss: yup
              .string()
              .trim()
              .matches(validDouble, t('message.onlyDecimalNumbers'))
              .typeError(t('message.onlyDecimalNumbers')),
            lossChargedInsurance: yup
              .string()
              .trim()
              .matches(validDouble, t('message.onlyDecimalNumbers'))
              .typeError(t('message.onlyDecimalNumbers')),
            lossChargedOther: yup
              .string()
              .trim()
              .matches(validDouble, t('message.onlyDecimalNumbers'))
              .typeError(t('message.onlyDecimalNumbers')),
            netLoss: yup
              .string()
              .trim()
              .matches(validDouble, t('message.onlyDecimalNumbers'))
              .typeError(t('message.onlyDecimalNumbers')),
            mitigationMeasures: yup
              .string()
              .trim()
              .required(t('message.fieldRequired'))
              .max(2000, t('message.max', {max: 2000})),
              hdRequestId: yup
                  .string()
                  .trim(),
            mitigationPercentage: yup
              .string()
              .trim()
              .matches(validDouble, t('message.onlyDecimalNumbers'))
              .typeError(t('message.onlyDecimalNumbers')),
            // additionalMitigationMeasures: yup.string().trim(),
            // .max(2000, t('message.max', {max: 2000})),
            notStartedReason: yup
              .string()
              .trim()
              .max(1000, t('message.max', {max: 1000})),
          })
        );
      }
    }
  }, [wathcLevelOfRealization]);

  const uploadOnChange = () => {
    const input = document.getElementById('upload');
    let children = [];
    if (input.files.length > 10) {
      Toast.fire({
        title: t('message.tooManyFiles'),
        icon: 'error',
      });
    } else {
      for (let i = 0; i < input.files.length; ++i) {
        if (input.files.item(i).size > 10485760) {
          Toast.fire({
            title: t('message.fileTooBig'),
            icon: 'error',
          });
        } else {
          children.push({
            id: input.files.item(i).name,
            label: input.files.item(i).name,
          });
          setFiles(children);
        }
      }
    }
  };

  const clearLocalFile = (index) => {
    const dt = new DataTransfer();
    const input = document.getElementById('upload');
    const {files} = input;
    let children = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (index !== i) {
        dt.items.add(file); // here you exclude the file. thus removing it.
        children.push({
          id: input.files.item(i).name,
          label: input.files.item(i).name,
        });
      }
    }
    input.files = dt.files;
    setFiles(children);
  };

  const onChangeGrossLoss = (e) => {
    let result = '';
    let valueNoText = e.target.value.replace(/[^0-9.]/, '');
    let valueDouble = valueNoText.match(/^(\d+(?:(.|,)\d{1,2})?).*/);
    if (valueDouble !== undefined && valueDouble !== null) {
      result = valueDouble[1];
    }
    setValue('grossLoss', result);
  };

  const onChangeLossChargedInsurance = (e) => {
    let result = '';
    let valueNoText = e.target.value.replace(/[^0-9.]/, '');
    let valueDouble = valueNoText.match(/^(\d+(?:(.|,)\d{1,2})?).*/);
    if (valueDouble !== undefined && valueDouble !== null) {
      result = valueDouble[1];
    }
    setValue('lossChargedInsurance', result);
  };

  const onChangeLossChargedOther = (e) => {
    let result = '';
    let valueNoText = e.target.value.replace(/[^0-9.]/, '');
    let valueDouble = valueNoText.match(/^(\d+(?:(.|,)\d{1,2})?).*/);
    if (valueDouble !== undefined && valueDouble !== null) {
      result = valueDouble[1];
    }
    setValue('lossChargedOther', result);
  };

  const onChangeMitigationPercentage = (e) => {
    let result = '';
    let valueNoText = e.target.value.replace(/[^0-9.]/, '');
    let valueDouble = valueNoText.match(/^(\d+(?:(.|,)\d{1,2})?).*/);
    if (valueDouble !== undefined && valueDouble !== null) {
      result = valueDouble[1];
    }
    setValue('mitigationPercentage', result);
  };

  const onChangeNetLoss = (e) => {
    let result = '';
    let valueNoText = e.target.value.replace(/[^0-9.]/, '');
    let valueDouble = valueNoText.match(/^(\d+(?:(.|,)\d{1,2})?).*/);
    if (valueDouble !== undefined && valueDouble !== null) {
      result = valueDouble[1];
    }
    setValue('netLoss', result);
  };

  const watchLossChargedInsurance = watch('lossChargedInsurance');
  const watchLossChargedOther = watch('lossChargedOther');
  const watchEstimatedGrossLost = watch('grossLoss');
  useEffect(() => {
    if(watchEstimatedGrossLost){
      let no = parseFloat(watchEstimatedGrossLost);
      setValue('netLoss', no.toFixed(2))
    }
    else if(watchEstimatedGrossLost){
      let no = parseFloat(watchEstimatedGrossLost) - parseFloat(watchLossChargedInsurance);
      setValue('netLoss', no.toFixed(2))
    }
    if (watchLossChargedInsurance && watchLossChargedOther) {
      let no =
        parseFloat(watchEstimatedGrossLost) -
        parseFloat(watchLossChargedInsurance) -
        parseFloat(watchLossChargedOther);
      setValue('netLoss', no.toFixed(2));
    }
   else if (watchLossChargedInsurance) {
      let no =
        parseFloat(watchEstimatedGrossLost) -
        parseFloat(watchLossChargedInsurance);
      setValue('netLoss', no.toFixed(2));
    }
    else if (watchLossChargedOther) {
      let no =
        parseFloat(watchEstimatedGrossLost) -
        parseFloat(watchLossChargedOther);
      setValue('netLoss', no.toFixed(2));
    }
  }, [watchLossChargedInsurance, watchEstimatedGrossLost, watchLossChargedOther]);

  return (
    <>
      {loading ? (
        <Spin spinning={loading} size={'large'} className="spinner" />
      ) : (
        <>
          {/* <TransitionModal open={}/> */}
          <PreventTransitionPrompt when={isAdding} />
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(submitForm)}>
              <Row className="mb-5 main-form">
                <Col xs={6} md={6} lg={3}>
                  <label className="label-measures">
                    {t('mitigation-measures.person')}
                  </label>
                  <SelectList
                    name="userResponsibleId"
                    className="select"
                    options={users}
                    defaultValue={{
                      value: userData.id,
                      label: userData.firstName + ' ' + userData.lastName,
                    }}
                    placeholder={t('placeholder.userResponsibleId')}
                    isClearable={false}
                  />
                  <label className="label-measures">
                    {t('mitigation-measures.analysis')}
                  </label>
                  <TextArea
                    name="riskAnalysis"
                    className={`form-control height text-area `}
                    placeholder={t('placeholder.riskAnalysis')}
                  />
                </Col>
                <Col xs={6} md={6} lg={3}>
                  <label className="label-measures">
                    {t('mitigation-measures.proposed')}
                  </label>
                  <TextArea
                    name="mitigationMeasures"
                    className={`form-control text-area `}
                    placeholder={t('placeholder.mitigationMeasures')}
                  />
                  <label className="label-measures">
                    {t('mitigation-measures.charge-of-implementing')}
                  </label>
                  <SelectInfinite
                      name="department"
                    api="/departments"
                    labelKey={l === 'mne' ? 'name' : 'nameEn'}
                    className="select"
                    placeholder={t('placeholder.departments')}
                    defaultValue={{
                      value: departmentData[0].id,
                      label: departmentData[0].name,
                    }}
                    isClearable={false}
                  />
                  <label className="label-measures">
                    {t('mitigation-measures.cost-estimation')}
                  </label>
                  <SelectInfinite
                    api="/cost-estimations"
                    name="cost"
                    labelKey="cost"
                    className="select"
                    placeholder={t('placeholder.costEstimation')}
                    queryKeys={'cost.contains'}
                  />
                  <label className="label-measures">
                    {t('mitigation-measures.date')}
                  </label>
                  <InputDate
                    name="realizationDate"
                    placeholderText={t('placeholder.pickDate')}
                    minDate={new Date()}
                    isClearable={true}
                  />
                </Col>
                <Col xs={6} md={6} lg={3}>
                  <label className="label-measures">
                    {t('mitigation-measures.result')}
                  </label>
                  <TextArea
                    name="resultOfApplied"
                    className={`form-control height text-area `}
                    placeholder={t('placeholder.result')}
                  />
                    <label className='label-measures'>
                        {t('mitigation-measures.IDHDRequest')}
                    </label>
                    <span className="optional register">
                    {t('common.optional')}
                  </span>
                    <InputText
                        type='string'
                        name='hdRequestId'
                        className='form-control input-field'
                        placeholder={t('placeholder.IDHDRequest')}
                        id='hdRequestId'
                    />
                  <label className="label-measures">
                    {t('mitigation-measures.mitigation-percentage')}
                  </label>
                  <span className="optional register">
                    {t('common.optional')}
                  </span>
                  <InputText
                    type="number"
                    name="mitigationPercentage"
                    className="form-control input-field"
                    placeholder={t('placeholder.mitigationPercentage')}
                    step=".01"
                    min="0"
                    onChange={onChangeMitigationPercentage}
                  />
                  <label className="label-measures">
                    {t('mitigation-measures.status')}
                  </label>
                  <SelectList
                    name="levelOfMeasureRealization"
                    className="select"
                    options={optionsTask}
                    placeholder={t('placeholder.levelOfMeasureRealization')}
                    isClearable={false}
                  />
                </Col>
                {/* {wathcLevelOfRealization?.value === 'NOTSTARTED' && ( */}
                <Col xs={6} md={6} lg={3}>
                  <label className="label-measures">
                    {t('mitigation-measures.reason')}
                  </label>
                  <TextArea
                    name="notStartedReason"
                    className={`form-control text-area `}
                    placeholder={t('placeholder.notStartedReason')}
                  />
                </Col>
                {/* )} */}

                <Col xs={6} md={6} lg={3}>
                  <label className="label-measures">
                    {t('mitigation-measures.loss-gross')}
                  </label>
                  <span className="optional register">
                    {t('common.optional')}
                  </span>
                  <InputText
                    type="number"
                    name="grossLoss"
                    className="form-control input-field"
                    placeholder={t('placeholder.lossGross')}
                    step="0.01"
                    onChange={onChangeGrossLoss}
                    defaultValue={
                      estimatedGrossLost != 'no-data' && estimatedGrossLost
                    }
                    disabled={estimatedGrossLost != 'no-data' && true}
                  />
                  <label className="label-measures">
                    {t('mitigation-measures.loss-collected')}
                  </label>
                  <span className="optional register">
                    {t('common.optional')}
                  </span>
                  <InputText
                    type="number"
                    name="lossChargedInsurance"
                    className="form-control input-field"
                    placeholder={t('placeholder.lossCollected')}
                    step="0.01"
                    onChange={onChangeLossChargedInsurance}
                  />
                  <label className="label-measures">
                    {t('mitigation-measures.loss-collected-other')}
                  </label>
                  <span className="optional register">
                    {t('common.optional')}
                  </span>
                  <InputText
                    type="number"
                    name="lossChargedOther"
                    className="form-control input-field"
                    placeholder={t('placeholder.lossCollectedOther')}
                    step="0.01"
                    onChange={onChangeLossChargedOther}
                  />
                  <label className="label-measures">
                    {t('mitigation-measures.loss-net')}
                  </label>
                  <span className="optional register">
                    {t('common.optional')}
                  </span>
                  <InputText
                    type="number"
                    name="netLoss"
                    className="form-control input-field"
                    placeholder={t('placeholder.lossNet')}
                    step="0.01"
                    onChange={onChangeNetLoss}
                    defaultValue={estimatedGrossLost ? estimatedGrossLost : ''}
                    disabled={true}
                  />
                </Col>
                <Col xs={6} md={6} lg={3}>
                  <label className="label-measures">
                    {t('mitigation-measures.proposalAdditional')}
                  </label>
                  <span className="optional register">
                    {t('common.optional')}
                  </span>
                  <InputText
                    type="text"
                    name="additionalMitigationMeasures"
                    className="form-control input-field"
                    placeholder={t('placeholder.mitigationMeasuresAdditional')}
                  />
                  <label className="label-measures">
                    {t('mitigation-measures.upload')}
                  </label>
                  <span className="optional register">
                    {t('common.optional')}
                  </span>
                  <InputText
                    id="upload"
                    type="file"
                    name="file"
                    multiple
                    className="file-input"
                    onChange={uploadOnChange}
                    accept={allowedFileTypes}
                  />
                  <div key={files}>
                    <ul>
                      {files?.map((file, index) => (
                        <li key={file.id}>
                          <div className="d-flex align-items-center">
                            <span className="mr-5">{file.label}</span>
                            <img
                              src={Trash}
                              alt="trash"
                              onClick={(e) => {
                                e.preventDefault();
                                clearLocalFile(index);
                              }}
                            />
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </Col>
              </Row>
              <Row className="mb-3 pt-3 right-btns">
                <Col lg={12} className="btns-float-right">
                  <button
                    className="btn btn-cancel btn-strong"
                    type="reset"
                    onClick={toggleOpen}
                  >
                    {t('buttons.cancelBtn')}
                  </button>
                  <button
                    className="btn btn-primary btn-strong"
                    disabled={disableBtn}
                  >
                    {t('buttons.saveBtn')}
                  </button>
                </Col>
              </Row>
            </form>
          </FormProvider>
        </>
      )}
    </>
  );
}

export default MeasuresFromAdd;
